/**
 * SentinelIntelPanel — runs live Sentinel-2 intelligence analysis on a drawn AOI.
 *
 * Capabilities:
 *  1. AOI Scan       — find latest Sentinel-2 scene, run YOLO, flag dark vessels
 *  2. MTI (diff)     — compare two Copernicus scenes to detect moved objects
 */
import { useState } from 'react'
import { Satellite, AlertTriangle, Eye, ArrowRight, Loader2, ChevronDown, ChevronUp, Anchor } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface Release {
  id: string | number
  date: string
  title: string
  source?: string
}

interface Props {
  aoi: Record<string, unknown> | null   // GeoJSON geometry of drawn AOI
  releases: Release[]                   // Available releases from the timeline
}

function getToken() {
  return localStorage.getItem('auth_token') || ''
}

function fmt(dateStr: string) {
  try { return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) }
  catch { return dateStr }
}

function CropImage({ b64, label }: { b64?: string; label?: string }) {
  if (!b64) return <div className="w-20 h-20 bg-slate-700 rounded flex items-center justify-center text-xs text-slate-500">No crop</div>
  return (
    <div className="relative">
      <img src={`data:image/jpeg;base64,${b64}`} alt={label || 'detection'} className="w-20 h-20 object-cover rounded border border-slate-600" />
      {label && <span className="absolute bottom-0 left-0 right-0 text-center text-[10px] bg-black/70 text-white px-0.5 rounded-b">{label}</span>}
    </div>
  )
}

function confBand(conf: number): { label: string; cls: string } {
  if (conf >= 0.6) return { label: 'HIGH', cls: 'text-green-400 border-green-700/50 bg-green-900/30' }
  if (conf >= 0.4) return { label: 'MED', cls: 'text-yellow-400 border-yellow-700/50 bg-yellow-900/30' }
  return { label: 'LOW', cls: 'text-red-400 border-red-700/50 bg-red-900/30' }
}

export default function SentinelIntelPanel({ aoi, releases }: Props) {
  const [scanResult, setScanResult] = useState<any>(null)
  const [mtiResult, setMtiResult] = useState<any>(null)
  const [scanning, setScanning] = useState(false)
  const [mtiRunning, setMtiRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mtiError, setMtiError] = useState<string | null>(null)
  const [mtiBeforeId, setMtiBeforeId] = useState<string>('')
  const [mtiAfterId, setMtiAfterId] = useState<string>('')
  const [expandedDets, setExpandedDets] = useState(false)
  const [confThreshold, setConfThreshold] = useState(0.35)

  const token = getToken()
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }

  // Only show Copernicus/Element84 scenes for MTI (need eosda view_id)
  const eosdaReleases = releases.filter(r => String(r.id).startsWith('eosda_') || r.source === 'eosda' || r.source === 'copernicus')

  async function runScan() {
    if (!aoi) return
    setScanning(true)
    setError(null)
    setScanResult(null)
    try {
      const res = await fetch('/api/intel/sentinel/scan', {
        method: 'POST',
        headers,
        body: JSON.stringify({ aoi, confidence_threshold: confThreshold, zoom: 16, cross_ref_ais: true }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || data.error || 'Scan failed')
      setScanResult(data)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setScanning(false)
    }
  }

  async function runMti() {
    if (!aoi || !mtiBeforeId || !mtiAfterId) return
    setMtiRunning(true)
    setMtiError(null)
    setMtiResult(null)
    try {
      const res = await fetch('/api/intel/sentinel/mti', {
        method: 'POST',
        headers,
        body: JSON.stringify({ aoi, scene_before_id: mtiBeforeId, scene_after_id: mtiAfterId, confidence_threshold: 0.20, zoom: 15 }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || data.error || 'MTI failed')
      setMtiResult(data)
    } catch (e: any) {
      setMtiError(e.message)
    } finally {
      setMtiRunning(false)
    }
  }

  if (!aoi) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center text-slate-500 gap-3">
        <Satellite className="w-10 h-10 opacity-40" />
        <p className="text-sm">Draw or select an AOI to run Sentinel-2 intelligence analysis</p>
      </div>
    )
  }

  return (
    <div className="space-y-5 p-4">

      {/* ── Capability 1: AOI Scan ── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Satellite className="w-4 h-4 text-green-400" />
            <span className="text-sm font-semibold text-slate-200">Sentinel-2 AOI Scan</span>
          </div>
          <Button size="sm" onClick={runScan} disabled={scanning}
            className="bg-green-600 hover:bg-green-500 text-white text-xs">
            {scanning ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Scanning...</> : 'Run Scan'}
          </Button>
        </div>
        <p className="text-xs text-slate-500">Finds latest cloud-free Sentinel-2 scene, runs YOLO detection, and cross-references vessel detections with live AIS to identify dark vessels.</p>

        {/* Confidence threshold control */}
        <div className="flex items-center gap-3 p-2 bg-slate-800/40 rounded border border-slate-700/40">
          <span className="text-xs text-slate-400 shrink-0">Min confidence:</span>
          <input
            type="range" min={0.15} max={0.75} step={0.05}
            value={confThreshold}
            onChange={e => setConfThreshold(parseFloat(e.target.value))}
            className="flex-1 accent-green-500 h-1.5"
          />
          <span className={`text-xs font-mono font-semibold shrink-0 ${confThreshold < 0.3 ? 'text-red-400' : confThreshold < 0.5 ? 'text-yellow-400' : 'text-green-400'}`}>
            {(confThreshold * 100).toFixed(0)}%
          </span>
        </div>
        {confThreshold < 0.3 && (
          <p className="text-[11px] text-red-400/80 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 shrink-0" />
            Low threshold — DOTA model will produce false positives on civilian urban imagery (rooftops, water tanks)
          </p>
        )}

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-900/30 border border-red-700/50 rounded text-xs text-red-300">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        {scanResult && !scanResult.error && (
          <div className="space-y-3">
            {/* Scene metadata */}
            <div className="p-3 bg-slate-800/60 rounded-lg border border-slate-700/50 text-xs space-y-1">
              <div className="flex items-center gap-1.5 text-green-400 font-medium mb-1">
                <Satellite className="w-3 h-3" />
                {scanResult.scene?.title || scanResult.scene?.id}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-400">
                <span>Date: <span className="text-slate-200">{fmt(scanResult.scene?.date || '')}</span></span>
                <span>Cloud: <span className="text-slate-200">{scanResult.scene?.cloud_coverage_pct?.toFixed(1)}%</span></span>
                <span>Tiles: <span className="text-slate-200">{scanResult.tiles_analyzed}</span></span>
                <span>Model: <span className="text-slate-200">{scanResult.model}</span></span>
              </div>
            </div>

            {/* Detection summary */}
            <div className="p-3 bg-slate-800/60 rounded-lg border border-slate-700/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-300 flex items-center gap-1.5">
                  <Eye className="w-3 h-3 text-blue-400" />
                  {scanResult.detection_count} objects detected
                </span>
                <button onClick={() => setExpandedDets(v => !v)} className="text-slate-500 hover:text-slate-300">
                  {expandedDets ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(scanResult.class_summary || {}).map(([cls, cnt]) => (
                  <span key={cls} className="px-2 py-0.5 bg-blue-900/40 border border-blue-700/50 rounded text-[11px] text-blue-300">
                    {cls} × {cnt as number}
                  </span>
                ))}
              </div>

              {/* Low confidence warning */}
              {(() => {
                const lowCount = (scanResult.detections || []).filter((d: any) => d.confidence < 0.4).length
                return lowCount > 0 ? (
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-400/80">
                    <AlertTriangle className="w-3 h-3 shrink-0" />
                    {lowCount} detection{lowCount > 1 ? 's' : ''} below 40% confidence — likely false positives on civilian imagery
                  </div>
                ) : null
              })()}

              {expandedDets && scanResult.detections?.length > 0 && (
                <div className="mt-3 space-y-3">
                  {/* High/medium confidence */}
                  {scanResult.detections.filter((d: any) => d.confidence >= 0.4).length > 0 && (
                    <div>
                      <p className="text-[10px] text-green-400/70 font-medium mb-1.5">Confirmed ({'>'}=40%)</p>
                      <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                        {scanResult.detections.filter((d: any) => d.confidence >= 0.4).slice(0, 20).map((d: any, i: number) => {
                          const band = confBand(d.confidence)
                          return (
                            <div key={i} className="flex flex-col items-center gap-0.5">
                              <CropImage b64={d.crop_b64} label={d.class_name} />
                              <span className={`text-[9px] px-1 rounded border ${band.cls}`}>{band.label} {(d.confidence * 100).toFixed(0)}%</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                  {/* Low confidence */}
                  {scanResult.detections.filter((d: any) => d.confidence < 0.4).length > 0 && (
                    <div>
                      <p className="text-[10px] text-red-400/70 font-medium mb-1.5">Low confidence (&lt;40%) — likely noise</p>
                      <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto opacity-60">
                        {scanResult.detections.filter((d: any) => d.confidence < 0.4).slice(0, 20).map((d: any, i: number) => (
                          <div key={i} className="flex flex-col items-center gap-0.5">
                            <CropImage b64={d.crop_b64} label={d.class_name} />
                            <span className="text-[9px] px-1 rounded border text-red-400 border-red-700/50 bg-red-900/30">LOW {(d.confidence * 100).toFixed(0)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Dark vessels */}
            {scanResult.dark_vessel_count > 0 && (
              <div className="p-3 bg-red-900/20 border border-red-700/50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Anchor className="w-4 h-4 text-red-400" />
                  <span className="text-sm font-semibold text-red-300">{scanResult.dark_vessel_count} Dark Vessel{scanResult.dark_vessel_count > 1 ? 's' : ''} Detected</span>
                  <span className="text-xs bg-red-800/50 px-1.5 py-0.5 rounded text-red-300">AIS-Silent</span>
                </div>
                <p className="text-xs text-slate-400 mb-3">Vessels visible on satellite imagery but not broadcasting AIS. Possible sanctions evasion, illegal fishing, or military activity.</p>
                <div className="space-y-2">
                  {scanResult.dark_vessels.map((dv: any, i: number) => (
                    <div key={i} className="flex items-center gap-3 p-2 bg-slate-800/50 rounded text-xs">
                      <CropImage b64={dv.crop_b64} label={dv.class_name} />
                      <div className="space-y-0.5 text-slate-300">
                        <div>Lat: {dv.lat?.toFixed(5)}, Lon: {dv.lon?.toFixed(5)}</div>
                        <div>Confidence: {(dv.confidence * 100).toFixed(1)}%</div>
                        {dv.nearest_ais_dist_m && (
                          <div className="text-slate-500">Nearest AIS: {dv.nearest_ais_dist_m}m away</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {scanResult.detection_count === 0 && (
              <p className="text-xs text-slate-500 text-center py-2">No objects detected in this AOI at the selected confidence level.</p>
            )}
          </div>
        )}

        {scanResult?.error && (
          <div className="text-xs text-red-400 p-2 bg-red-900/20 rounded">{scanResult.error}</div>
        )}
      </section>

      <div className="border-t border-slate-700/50" />

      {/* ── Capability 2: MTI ── */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <ArrowRight className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-semibold text-slate-200">Moving Target Indicator (MTI)</span>
        </div>
        <p className="text-xs text-slate-500">Compare two Sentinel-2 scenes of this AOI to detect objects that moved, appeared, or disappeared between captures.</p>

        {eosdaReleases.length < 2 ? (
          <div className="flex items-start gap-2 p-3 bg-slate-800/40 border border-slate-700/40 rounded text-xs text-slate-400">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500/70" />
            <span>
              <span className="text-amber-400/90 font-medium">Not ready.</span> MTI requires at least 2 Sentinel-2 scenes loaded for this AOI.
              Go to the <span className="text-slate-200 font-medium">Timeline</span> tab, select your AOI, and fetch 2 or more Copernicus scenes — then the MTI controls will appear here.
              {eosdaReleases.length === 1 && <span className="text-slate-300"> (1 scene loaded — need 1 more)</span>}
              {eosdaReleases.length === 0 && <span className="text-slate-300"> (no EOSDA/Copernicus scenes loaded yet)</span>}
            </span>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Before scene</label>
                <select value={mtiBeforeId} onChange={e => setMtiBeforeId(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-xs text-slate-200">
                  <option value="">Select...</option>
                  {eosdaReleases.map(r => (
                    <option key={String(r.id)} value={String(r.id)}>{fmt(r.date)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">After scene</label>
                <select value={mtiAfterId} onChange={e => setMtiAfterId(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-xs text-slate-200">
                  <option value="">Select...</option>
                  {eosdaReleases.map(r => (
                    <option key={String(r.id)} value={String(r.id)}>{fmt(r.date)}</option>
                  ))}
                </select>
              </div>
            </div>
            <Button size="sm" onClick={runMti} disabled={mtiRunning || !mtiBeforeId || !mtiAfterId}
              className="bg-purple-600 hover:bg-purple-500 text-white text-xs w-full">
              {mtiRunning ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Analyzing...</> : 'Run MTI Analysis'}
            </Button>
          </div>
        )}

        {mtiError && (
          <div className="text-xs text-red-400 p-2 bg-red-900/20 rounded">{mtiError}</div>
        )}

        {mtiResult && !mtiResult.error && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              {[
                { label: 'Moved', count: mtiResult.moved_count, color: 'text-yellow-400' },
                { label: 'Appeared', count: mtiResult.appeared_count, color: 'text-green-400' },
                { label: 'Disappeared', count: mtiResult.disappeared_count, color: 'text-red-400' },
              ].map(({ label, count, color }) => (
                <div key={label} className="bg-slate-800/60 rounded p-2 border border-slate-700/50">
                  <div className={`text-xl font-bold ${color}`}>{count}</div>
                  <div className="text-slate-400">{label}</div>
                </div>
              ))}
            </div>

            {mtiResult.moved_targets?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-yellow-400 mb-2">Moved Targets</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {mtiResult.moved_targets.map((mt: any, i: number) => (
                    <div key={i} className="p-2 bg-slate-800/60 rounded border border-yellow-700/30 text-xs">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-1.5 py-0.5 bg-yellow-900/50 rounded text-yellow-300">{mt.class_name}</span>
                        <span className="text-slate-400">{mt.distance_m}m displacement</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CropImage b64={mt.crop_before} label="Before" />
                        <ArrowRight className="w-4 h-4 text-slate-500 shrink-0" />
                        <CropImage b64={mt.crop_after} label="After" />
                        <div className="text-slate-400 text-[10px] leading-4">
                          <div>From: {mt.lat_before?.toFixed(4)}, {mt.lon_before?.toFixed(4)}</div>
                          <div>To:&nbsp;&nbsp; {mt.lat_after?.toFixed(4)}, {mt.lon_after?.toFixed(4)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {mtiResult.moved_count === 0 && mtiResult.appeared_count === 0 && mtiResult.disappeared_count === 0 && (
              <p className="text-xs text-slate-500 text-center py-2">No significant object changes detected between these scenes.</p>
            )}
          </div>
        )}
      </section>
    </div>
  )
}
